import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@apollo/client/react';

import menuItems from 'src/utils/data/menu-items';
import { mergeMenuCounts } from 'src/utils/data/mergeMenuCounts';
import { getMenuCounts } from 'src/services/getMenuCounts';
import { GET_ME } from 'src/api/graphql/queries/me.gql';
import { normalizeUserRole } from 'src/utils/mappers/user.mapper';
import { buildNoAccessPath } from 'src/routes/constants';
import { isUnauthorizedError } from 'src/utils/helpers/isUnauthorizedError';

const filterMenuItemsByRole = (items, role) =>
	items.map((group) => ({
		...group,
		children: (group.children ?? []).filter((item) => {
			if (!item.roles?.length) return true;

			return role && item.roles.includes(role);
		}),
	}));

const useMenuItems = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const { data: meData } = useQuery(GET_ME);
	const [counts, setCounts] = useState({});
	const [isLoading, setIsLoading] = useState(true);

	const role = normalizeUserRole(meData?.me?.role);

	useEffect(() => {
		let cancelled = false;

		setIsLoading(true);

		getMenuCounts()
			.then((data) => {
				if (!cancelled) {
					setCounts(data);
					setIsLoading(false);
				}
			})
			.catch((error) => {
				if (isUnauthorizedError(error)) {
					navigate(
						buildNoAccessPath(
							`${location.pathname}${location.search}${location.hash}`,
						),
						{ replace: true },
					);
					return;
				}

				console.error('Failed to load menu counts. Error: ', error);

				if (!cancelled) {
					setIsLoading(false);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [location.pathname, location.search, location.hash, navigate]);

	const items = useMemo(
		() =>
			mergeMenuCounts(
				filterMenuItemsByRole(menuItems, role),
				counts,
				isLoading,
			),
		[counts, isLoading, role],
	);

	return {
		menuItems: items,
		menuLoading: isLoading,
	};
};

export default useMenuItems;
